/**
 * Ink and screen colour, converted the way the press file is.
 *
 * The tables are Ghostscript's own answers (make-colour-tables.cjs), so a
 * colour chosen here is the ink that prints like it, and ink shown here looks
 * as the PDF shows it. The simple formulas this replaced turned any colour
 * with no green in it into 100% magenta: an electric blue-violet and a purple
 * became nearly the same purple on paper, and a blue-to-purple gradient
 * printed as one flat colour while the designer still showed the blue.
 */

import type { Ink } from '../model/types';
import { CMYK_STEPS, RGB_STEPS, TO_INK, TO_SCREEN } from './tables';

const decode = ( text: string ): Uint8Array => {
  const binary = atob( text );
  const out = new Uint8Array( binary.length );

  for ( let i = 0; i < binary.length; i++ ) out[ i ] = binary.charCodeAt( i );

  return out;
};

let screenTable: Uint8Array | null = null;
let inkTable: Uint8Array | null = null;

const clamp01 = ( v: number ) => ( v < 0 ? 0 : v > 1 ? 1 : Number.isFinite( v ) ? v : 0 );

/** How ink looks, as 0-255 sRGB. */
export function inkToRgb( ink: Ink ): [ number, number, number ] {
  screenTable ??= decode( TO_SCREEN );

  const n = CMYK_STEPS - 1;
  const pos = ink.map( ( v ) => clamp01( v ) * n );
  const base = pos.map( ( p ) => Math.min( n - 1, Math.floor( p ) ) );
  const frac = pos.map( ( p, i ) => p - ( base[ i ] as number ) );
  const out: [ number, number, number ] = [ 0, 0, 0 ];

  // Sixteen corners of the surrounding cell, weighted.
  for ( let corner = 0; corner < 16; corner++ ) {
    let weight = 1;
    let index = 0;

    for ( let ch = 0; ch < 4; ch++ ) {
      const up = ( corner >> ( 3 - ch ) ) & 1;
      const f = frac[ ch ] as number;

      weight *= up ? f : 1 - f;
      index = index * CMYK_STEPS + ( base[ ch ] as number ) + up;
    }

    if ( weight === 0 ) continue;

    for ( let c = 0; c < 3; c++ ) out[ c ] = ( out[ c ] as number ) + weight * ( screenTable[ index * 3 + c ] as number );
  }

  return out.map( ( v ) => Math.round( v ) ) as [ number, number, number ];
}

/**
 * The ink that prints like a screen colour (0-255 sRGB).
 *
 * Greys, black most of all, go to black ink alone. A profile gives #000000 as
 * something like 75/68/67/90: four plates for what the customer calls black,
 * and the most common way small type comes back fuzzy.
 */
export function rgbToInk( r: number, g: number, b: number ): Ink {
  const round = ( v: number ) => Math.round( clamp01( v ) * 1000 ) / 1000;

  if ( Math.max( r, g, b ) - Math.min( r, g, b ) <= 3 ) {
    return [ 0, 0, 0, round( 1 - ( r + g + b ) / 3 / 255 ) ];
  }

  inkTable ??= decode( TO_INK );

  const n = RGB_STEPS - 1;
  const pos = [ r, g, b ].map( ( v ) => ( Math.min( 255, Math.max( 0, v ) ) / 255 ) * n );
  const base = pos.map( ( p ) => Math.min( n - 1, Math.floor( p ) ) );
  const frac = pos.map( ( p, i ) => p - ( base[ i ] as number ) );
  const out = [ 0, 0, 0, 0 ];

  for ( let corner = 0; corner < 8; corner++ ) {
    let weight = 1;
    let index = 0;

    for ( let ch = 0; ch < 3; ch++ ) {
      const up = ( corner >> ( 2 - ch ) ) & 1;
      const f = frac[ ch ] as number;

      weight *= up ? f : 1 - f;
      index = index * RGB_STEPS + ( base[ ch ] as number ) + up;
    }

    if ( weight === 0 ) continue;

    for ( let c = 0; c < 4; c++ ) out[ c ] = ( out[ c ] as number ) + weight * ( inkTable[ index * 4 + c ] as number );
  }

  return out.map( ( v ) => round( v / 255 ) ) as Ink;
}

/** A hex colour for ink, as it prints. */
export function inkToHex( ink: Ink ): string {
  return '#' + inkToRgb( ink ).map( ( v ) => v.toString( 16 ).padStart( 2, '0' ) ).join( '' );
}
